<template>
  <div class="class-diagnosis">
    <el-card shadow="never">
      <div class="config-section">
        <el-form inline>
          <el-form-item label="年级">
            <el-select v-model="filters.grade" style="width: 110px">
              <el-option v-for="g in availableGrades" :key="g" :label="g" :value="g" />
            </el-select>
          </el-form-item>
          <el-form-item label="排序">
            <el-select v-model="filters.sortBy" style="width: 150px">
              <el-option label="总均分差值" value="total_diff" />
              <el-option label="薄弱学科数" value="weak_count" />
              <el-option label="班级排名" value="rank" />
            </el-select>
          </el-form-item>
          <el-form-item label="关注阈值">
            <el-input-number v-model="filters.gapThreshold" :min="0" :max="30" :step="1" style="width: 120px" />
          </el-form-item>
          <el-form-item>
            <el-button type="primary" :icon="DataAnalysis" @click="fetchOverview" :loading="loading" :disabled="!semesterId || !examType">
              生成诊断
            </el-button>
          </el-form-item>
        </el-form>
      </div>

      <div v-if="loading" class="loading-wrap">
        <el-skeleton :rows="8" animated />
      </div>
      <el-empty v-else-if="!hasGradeData" description="暂无班级诊断数据" />

      <div v-else class="diagnosis-content">
        <div class="summary-grid">
          <div class="summary-item">
            <span class="summary-label">年级总均分</span>
            <strong>{{ gradeData.grade_total_avg }}</strong>
          </div>
          <div class="summary-item">
            <span class="summary-label">领先班级</span>
            <strong>{{ leadingClassLabel }}</strong>
          </div>
          <div class="summary-item warning">
            <span class="summary-label">需关注班级</span>
            <strong>{{ attentionCount }} 个</strong>
          </div>
          <div class="summary-item">
            <span class="summary-label">分析学科</span>
            <strong>{{ gradeData.subjects.length }} 门</strong>
          </div>
        </div>

        <div class="chart-grid">
          <div class="chart-panel">
            <div class="panel-title">班级总均分相对年级差值</div>
            <v-chart class="chart" :option="diffChartOption" autoresize />
          </div>
          <div class="chart-panel">
            <div class="panel-title">学科差值热力图</div>
            <v-chart class="chart" :option="heatmapOption" autoresize />
          </div>
        </div>

        <el-table :data="tableRows" border stripe class="diagnosis-table" :header-cell-style="{ background: '#f8fafc', color: '#1e293b', fontWeight: 'bold' }">
          <el-table-column prop="class_name" label="班级" width="90" fixed align="center" />
          <el-table-column prop="student_count" label="人数" width="70" align="center" />
          <el-table-column prop="rank" label="排名" width="70" align="center" />
          <el-table-column prop="total_avg" label="总均分" width="90" align="center">
            <template #default="{ row }">
              <strong>{{ row.total_avg }}</strong>
            </template>
          </el-table-column>
          <el-table-column label="总分差值" width="105" align="center" sortable sort-by="total_diff">
            <template #default="{ row }">
              <el-tag :type="diffTagType(row.total_diff)" effect="plain">
                {{ formatDiff(row.total_diff) }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column label="语数外差值" width="110" align="center" sortable sort-by="sanke_diff">
            <template #default="{ row }">{{ formatDiff(row.sanke_diff) }}</template>
          </el-table-column>
          <el-table-column label="五科差值" width="100" align="center" sortable sort-by="wuke_diff">
            <template #default="{ row }">{{ formatDiff(row.wuke_diff) }}</template>
          </el-table-column>
          <el-table-column label="优势学科" min-width="180">
            <template #default="{ row }">
              <div class="subject-tags">
                <el-tag v-for="item in row.strengths" :key="item.subject" type="success" size="small">
                  {{ item.subject }} {{ formatDiff(item.diff) }}
                </el-tag>
                <span v-if="row.strengths.length === 0" class="muted">暂无明显优势</span>
              </div>
            </template>
          </el-table-column>
          <el-table-column label="薄弱学科" min-width="210">
            <template #default="{ row }">
              <div class="subject-tags">
                <el-tooltip v-for="item in row.weaknesses" :key="item.subject" placement="top" :content="teacherTip(row, item.subject)">
                  <el-tag :type="Math.abs(item.diff) >= filters.gapThreshold ? 'danger' : 'warning'" size="small">
                    {{ item.subject }} {{ formatDiff(item.diff) }}
                  </el-tag>
                </el-tooltip>
                <span v-if="row.weaknesses.length === 0" class="muted">暂无明显短板</span>
              </div>
            </template>
          </el-table-column>
        </el-table>
      </div>
    </el-card>
  </div>
</template>

<script setup>
import { ref, reactive, computed, watch, defineProps } from 'vue'
import VChart from 'vue-echarts'
import '@/utils/echarts'
import { DataAnalysis } from '@element-plus/icons-vue'
import { ElMessage } from 'element-plus'
import { gradeAnalysisApi } from '@/api'

const props = defineProps({
  semesterId: { type: Number, required: false },
  examType: { type: String, required: false },
  overviewData: { type: Object, required: false },
  availableGrades: { type: Array, default: () => [] }
})

const loading = ref(false)
const localOverview = ref(null)

const filters = reactive({
  grade: props.availableGrades[0] || '',
  sortBy: 'total_diff',
  gapThreshold: 5
})

const sourceData = computed(() => props.overviewData || localOverview.value || {})
const gradeData = computed(() => sourceData.value[filters.grade] || null)
const hasGradeData = computed(() => Boolean(gradeData.value && gradeData.value.classes && gradeData.value.classes.length))

const formatDiff = (value) => {
  const rounded = Number(value || 0).toFixed(1)
  return value > 0 ? `+${rounded}` : rounded
}

const diffTagType = (value) => {
  if (value >= filters.gapThreshold) return 'success'
  if (value <= -filters.gapThreshold) return 'danger'
  return 'info'
}

const buildClassRow = (classItem) => {
  const data = gradeData.value
  const subjectDiffs = data.subjects.map(subject => ({
    subject,
    teacher: classItem.teachers?.[subject] || '',
    avg: classItem.averages?.[subject] ?? 0,
    grade_avg: data.grade_averages?.[subject] ?? 0,
    diff: Number(((classItem.averages?.[subject] ?? 0) - (data.grade_averages?.[subject] ?? 0)).toFixed(1))
  }))

  const strengths = subjectDiffs
    .filter(item => item.diff > 0)
    .sort((a, b) => b.diff - a.diff)
    .slice(0, 3)

  const weaknesses = subjectDiffs
    .filter(item => item.diff < 0)
    .sort((a, b) => a.diff - b.diff)
    .slice(0, 3)

  return {
    ...classItem,
    total_diff: Number((classItem.total_avg - data.grade_total_avg).toFixed(1)),
    sanke_diff: Number((classItem.sanke_avg - data.grade_sanke_avg).toFixed(1)),
    wuke_diff: Number((classItem.wuke_avg - data.grade_wuke_avg).toFixed(1)),
    subject_diffs: subjectDiffs,
    strengths,
    weaknesses,
    weak_count: subjectDiffs.filter(item => item.diff <= -filters.gapThreshold).length
  }
}

const tableRows = computed(() => {
  if (!hasGradeData.value) return []
  const rows = gradeData.value.classes.map(buildClassRow)
  const sorters = {
    total_diff: (a, b) => a.total_diff - b.total_diff,
    weak_count: (a, b) => b.weak_count - a.weak_count || a.total_diff - b.total_diff,
    rank: (a, b) => a.rank - b.rank
  }
  return rows.sort(sorters[filters.sortBy])
})

const leadingClassLabel = computed(() => {
  if (!hasGradeData.value) return '-'
  const best = [...gradeData.value.classes].sort((a, b) => b.total_avg - a.total_avg)[0]
  return `${best.class_name} ${formatDiff(best.total_avg - gradeData.value.grade_total_avg)}`
})

const attentionCount = computed(() => tableRows.value.filter(row => row.total_diff <= -filters.gapThreshold).length)

const diffChartOption = computed(() => {
  if (!hasGradeData.value) return {}
  const rows = [...tableRows.value].sort((a, b) => b.total_diff - a.total_diff)
  return {
    tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
    grid: { left: 40, right: 20, top: 24, bottom: 40 },
    xAxis: { type: 'category', data: rows.map(row => row.class_name) },
    yAxis: { type: 'value', name: '分差' },
    series: [{
      name: '总均分差值',
      type: 'bar',
      data: rows.map(row => row.total_diff),
      itemStyle: {
        color: params => params.value >= 0 ? '#059669' : '#dc2626',
        borderRadius: [4, 4, 0, 0]
      },
      label: { show: true, position: 'top', formatter: params => formatDiff(params.value) }
    }]
  }
})

const heatmapOption = computed(() => {
  if (!hasGradeData.value) return {}
  const rows = tableRows.value
  const subjects = gradeData.value.subjects
  const heatData = []
  rows.forEach((row, classIndex) => {
    subjects.forEach((subject, subjectIndex) => {
      const item = row.subject_diffs.find(diff => diff.subject === subject)
      heatData.push([subjectIndex, classIndex, item?.diff || 0])
    })
  })

  return {
    tooltip: {
      position: 'top',
      formatter: params => {
        const subject = subjects[params.value[0]]
        const row = rows[params.value[1]]
        return `${row.class_name} ${subject}<br/>较年级均分：${formatDiff(params.value[2])}`
      }
    },
    grid: { left: 70, right: 20, top: 20, bottom: 40 },
    xAxis: { type: 'category', data: subjects, splitArea: { show: true } },
    yAxis: { type: 'category', data: rows.map(row => row.class_name), splitArea: { show: true } },
    visualMap: {
      min: -20,
      max: 20,
      calculable: true,
      orient: 'horizontal',
      left: 'center',
      bottom: 0,
      inRange: { color: ['#dc2626', '#f8fafc', '#059669'] }
    },
    series: [{
      name: '学科差值',
      type: 'heatmap',
      data: heatData,
      label: { show: true, formatter: params => formatDiff(params.value[2]) },
      emphasis: { itemStyle: { shadowBlur: 10, shadowColor: 'rgba(0, 0, 0, 0.25)' } }
    }]
  }
})

const teacherTip = (row, subject) => {
  const teacher = row.teachers?.[subject]
  return teacher ? `${subject} 任课：${teacher}` : `${subject} 暂无任课教师配置`
}

const fetchOverview = async () => {
  if (!props.semesterId || !props.examType) return
  loading.value = true
  try {
    localOverview.value = await gradeAnalysisApi.getOverview(props.semesterId, props.examType)
    if (!hasGradeData.value) {
      ElMessage.warning('该条件下暂无成绩总览数据')
    }
  } catch (e) {
    console.error(e)
  } finally {
    loading.value = false
  }
}

watch(() => [props.semesterId, props.examType], () => {
  localOverview.value = null
})
</script>

<style scoped>
.config-section {
  margin-bottom: 16px;
  padding-bottom: 16px;
  border-bottom: 1px solid #f3f4f6;
}

.loading-wrap {
  padding: 24px;
}

.diagnosis-content {
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.summary-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(170px, 1fr));
  gap: 12px;
}

.summary-item {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 14px 16px;
  background: #f8fafc;
}

.summary-item.warning {
  background: #fff7ed;
  border-color: #fed7aa;
}

.summary-label {
  display: block;
  font-size: 13px;
  color: #6b7280;
  margin-bottom: 6px;
}

.summary-item strong {
  font-size: 20px;
  color: #111827;
}

.chart-grid {
  display: grid;
  grid-template-columns: minmax(360px, 1fr) minmax(420px, 1.2fr);
  gap: 16px;
}

.chart-panel {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 14px;
  min-height: 360px;
}

.panel-title {
  font-weight: 600;
  color: #111827;
  margin-bottom: 10px;
}

.chart {
  width: 100%;
  height: 320px;
}

.diagnosis-table {
  width: 100%;
}

.subject-tags {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
}

.muted {
  color: #9ca3af;
  font-size: 13px;
}

@media (max-width: 980px) {
  .chart-grid {
    grid-template-columns: 1fr;
  }
}
</style>
